import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../contexts/AuthContext';

// Generate initials with SS rule
const generateInitials = (firstName, lastName) => {
  if (!firstName || !lastName) return '';

  const firstInitial = firstName.charAt(0).toUpperCase();
  const lastInitial = lastName.charAt(0).toUpperCase();

  // Special case: If both initials are S, use first 2 letters of first name
  if (firstInitial === 'S' && lastInitial === 'S') {
    return firstName.substring(0, 2).charAt(0).toUpperCase() +
           firstName.substring(1, 2).toLowerCase();
  }

  return firstInitial + lastInitial;
};

const StaffMemberSelector = ({ inline = false }) => {
  const { user } = useAuth();
  const [selectedStaffMember, setSelectedStaffMember] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Check if user is staff account
  const isStaffAccount = user && user.accountType === 'staff';
  const staffMembers = (user?.staffMembers || []).filter(m => m.active);

  // Load selected staff member from localStorage on mount and listen for changes
  useEffect(() => {
    if (!isStaffAccount) return;

    const loadSelectedStaffMember = () => {
      const saved = localStorage.getItem('selectedStaffMember');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          // Verify the staff member still exists in the current list
          const exists = staffMembers.find(m => m.id === parsed.id);
          if (exists) {
            setSelectedStaffMember(parsed);
          } else {
            // Clear if staff member no longer exists
            localStorage.removeItem('selectedStaffMember');
            setSelectedStaffMember(null);
          }
        } catch (e) {
          localStorage.removeItem('selectedStaffMember');
          setSelectedStaffMember(null);
        }
      } else {
        setSelectedStaffMember(null);
      }
    };

    // Load on mount
    loadSelectedStaffMember();

    // Listen for storage events (from other components or tabs)
    const handleStorageChange = () => {
      loadSelectedStaffMember();
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [isStaffAccount, staffMembers]);

  const handleSelectStaffMember = (member) => {
    const memberWithInitials = {
      ...member,
      initials: member.initials || generateInitials(member.firstName, member.lastName)
    };
    setSelectedStaffMember(memberWithInitials);
    localStorage.setItem('selectedStaffMember', JSON.stringify(memberWithInitials));
    // Trigger a storage event for Sidebar to pick up
    window.dispatchEvent(new Event('storage'));
    setShowModal(false);
  };

  // Keyboard shortcuts for staff selection (1-9, 0 for 10th)
  useEffect(() => {
    if (!showModal) return;

    const handleKeyPress = (e) => {
      const num = parseInt(e.key);
      if (num >= 1 && num <= 9) {
        const index = num - 1;
        if (staffMembers[index]) {
          handleSelectStaffMember(staffMembers[index]);
        }
      } else if (e.key === '0') {
        // 0 key selects the 10th staff member (index 9)
        if (staffMembers[9]) {
          handleSelectStaffMember(staffMembers[9]);
        }
      } else if (e.key === 'Escape') {
        setShowModal(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showModal, staffMembers]);

  // Don't show for non-staff accounts
  if (!isStaffAccount || staffMembers.length === 0) {
    return null;
  }

  return (
    <>
      {/* Banner with selected staff or "click to select" button */}
      {inline ? (
        /* Inline mode for header */
        <div className="flex items-center gap-2">
          {selectedStaffMember ? (
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-3 px-4 py-2 bg-floral-50 border-2 border-floral-200 rounded-lg hover:bg-floral-100 transition-colors shadow-sm"
              title="Click to change staff member"
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium text-sm"
                style={{ backgroundColor: selectedStaffMember.avatarColor || '#10b981' }}
              >
                {selectedStaffMember.initials || generateInitials(selectedStaffMember.firstName, selectedStaffMember.lastName)}
              </div>
              <span className="text-base font-semibold text-gray-800">
                {selectedStaffMember.firstName} {selectedStaffMember.lastName}
              </span>
              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
              </svg>
            </button>
          ) : (
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-amber-100 border-2 border-amber-300 text-amber-700 rounded-lg hover:bg-amber-200 [html[data-scheme=dark]:not([data-appearance=fable])_&]:bg-warn-bg [html[data-scheme=dark]:not([data-appearance=fable])_&]:border-warn-fg/40 [html[data-scheme=dark]:not([data-appearance=fable])_&]:text-warn-fg [html[data-scheme=dark]:not([data-appearance=fable])_&]:hover:bg-warn-bg transition-colors font-semibold shadow-sm"
              title="Select staff member"
            >
              ⚠️ Select Staff Member
            </button>
          )}
        </div>
      ) : (
        /* Full banner mode */
        <div className="bg-gradient-to-r from-floral-50 to-floral-100 border border-floral-200 rounded-lg px-4 py-2.5 mb-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">Creating order as:</span>

              {selectedStaffMember ? (
                <button
                  onClick={() => setShowModal(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium text-sm"
                    style={{ backgroundColor: selectedStaffMember.avatarColor || '#10b981' }}
                  >
                    {selectedStaffMember.initials || generateInitials(selectedStaffMember.firstName, selectedStaffMember.lastName)}
                  </div>
                  <span className="font-medium text-gray-800">
                    {selectedStaffMember.firstName} {selectedStaffMember.lastName}
                  </span>
                  <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                  </svg>
                </button>
              ) : (
                <button
                  onClick={() => setShowModal(true)}
                  className="px-4 py-2 bg-floral-600 text-white rounded-lg hover:bg-floral-700 transition-colors font-medium"
                >
                  Select Staff Member
                </button>
              )}
            </div>

            {!selectedStaffMember && (
              <span className="text-xs text-amber-600 font-medium">⚠️ Please select a staff member to continue</span>
            )}
          </div>
        </div>
      )}

      {/* Modal - Using Portal to render at document root */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-floral-50 to-floral-100">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900">Select Staff Member</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-1 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Click or press <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs font-mono">1-9, 0</kbd> for quick selection
              </p>
            </div>

            {/* Staff Members Grid */}
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-120px)]">
              <div className="grid grid-cols-2 gap-3">
                {staffMembers.map((member, index) => {
                  const isSelected = selectedStaffMember?.id === member.id;
                  const initials = member.initials || generateInitials(member.firstName, member.lastName);
                  // Show 1-9 for first 9 members, 0 for 10th member, nothing after
                  const shortcutKey = index < 9 ? index + 1 : (index === 9 ? 0 : null);

                  return (
                    <button
                      key={member.id}
                      onClick={() => handleSelectStaffMember(member)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-left ${
                        isSelected
                          ? 'bg-floral-50 border-2 border-floral-500 shadow-md'
                          : 'bg-gray-50 border-2 border-gray-200 hover:border-floral-300 hover:bg-floral-50'
                      }`}
                    >
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white font-medium text-lg flex-shrink-0"
                        style={{ backgroundColor: member.avatarColor || '#10b981' }}
                      >
                        {initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {member.firstName} {member.lastName}
                        </p>
                        <p className="text-sm text-gray-500">{initials}</p>
                      </div>
                      {shortcutKey !== null && (
                        <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-sm font-mono text-gray-600 flex-shrink-0">
                          {shortcutKey}
                        </kbd>
                      )}
                      {isSelected && (
                        <svg className="w-5 h-5 text-floral-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default StaffMemberSelector;
